import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Slider from "react-slick";

import {
    AppBar,
    Toolbar,
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Fab,
    Tooltip,
    Link,
    CssBaseline,
} from "@mui/material";

import { ThemeProvider, createTheme } from "@mui/material/styles";

import { getSiteConfig } from "./siteRegistry";
import { submitContactForm } from "./services/api";

// slick-carousel CSS
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function normalizeHref(href) {
    if (!href) return null;
    return href; // allow relative (/Images/...) and absolute
}

function buildBackgroundStyle(bg) {
    if (!bg) return {};
    if (typeof bg === "string") return { background: bg };

    const style = {};
    if (bg.color) style.background = bg.color;

    if (bg.image) {
        style.backgroundImage = `url(${bg.image})`;
        style.backgroundRepeat = "no-repeat";
        style.backgroundSize = "cover";
        style.backgroundPosition = bg.verticalPosition
            ? `center ${bg.verticalPosition}`
            : "center center";
    }

    if (bg.fullHeight) {
        style.minHeight = "calc(100vh - 64px)";
    }

    return style;
}
function splitName(name) {
    if (!name || typeof name !== "string") {
        return { firstName: "", lastName: "" };
    }

    const trimmed = name.trim();
    const parts = trimmed.split(/\s+/); // handles multiple spaces

    if (parts.length === 1) {
        return {
            firstName: parts[0],
            lastName: ""
        };
    }

    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(" ")
    };
}

function ContactDialog({ open, onClose, site, siteId }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");
    const { firstName, lastName } = splitName(name);
    async function handleSend() {
        setErr("");
        setSending(true);
        var request = { PageID: siteId, FirstName: firstName, LastName: lastName, Email: email, Comment: message  }
        try {
            await submitContactForm(request);
            setSent(true);
        } catch (e) {
            setErr(e?.message || "Failed to send message.");
        } finally {
            setSending(false);
        }
    }

    function handleClose() {
        setSent(false);
        setErr("");
        setName("");
        setEmail("");
        setMessage("");
        onClose?.();
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{site?.contactButtonLabel || "Contact"}</DialogTitle>
            <DialogContent dividers>
                {sent ? (
                    <Typography>Message sent. Thank you!</Typography>
                ) : (
                    <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            fullWidth
                            multiline
                            minRows={4}
                        />
                        {err ? <Typography color="error">{err}</Typography> : null}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
                {!sent ? (
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending || !name || !email || !message}
                    >
                        {sending ? "Sending..." : "Send"}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}

function HeaderSection({ site, section, onContactClick }) {
    // no hooks here, safe to early-return
    if (!section?.display) return null;

    const bg = section.background || site?.background || "#111";
    const logo = section.logo || site?.logo || null;
    const logoHeight = section.logoHeight || "64px";
    const fixed = !!section.fixed;

    const action = section.actionButton;
    const actionHref = normalizeHref(action?.href);
    const actionLabel = action?.label;

    return (
        <AppBar
            position={fixed ? "fixed" : "static"}
            elevation={section.shadow ? 6 : 0}
            sx={{ background: bg }}
        >
            <Toolbar sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {logo ? (
                        <img
                            src={logo}
                            alt={section.alt || site?.pageTitle || site?.id}
                            style={{ height: logoHeight }}
                        />
                    ) : (
                        <Typography variant="h6" sx={{ color: "#fff" }}>
                            {site?.shortTitle || site?.pageTitle || site?.id}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {site?.floatingContactInfoButton || section?.contactButton ? (
                        <Button
                            variant="contained"
                            onClick={onContactClick}
                            sx={{
                                background: site?.buttonColor || "#fff",
                                color: site?.buttonColorText || "#111",
                                border: site?.buttonBorder || "none",
                            }}
                        >
                            {site?.contactButtonLabel || "Contact"}
                        </Button>
                    ) : null}

                    {actionLabel ? (
                        actionHref ? (
                            <Button
                                variant="contained"
                                component="a"
                                href={actionHref}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                    background: action?.background || "#444",
                                    color: action?.color || "#fff",
                                }}
                                title={action?.tooltip || ""}
                            >
                                {actionLabel}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                sx={{
                                    background: action?.background || "#444",
                                    color: action?.color || "#fff",
                                }}
                                title={action?.tooltip || ""}
                                onClick={() => {
                                    console.warn(
                                        "Action button clicked but no href provided in registry:",
                                        action
                                    );
                                }}
                            >
                                {actionLabel}
                            </Button>
                        )
                    ) : null}
                </Box>
            </Toolbar>
        </AppBar>
    );
}

function MainSection({ site, section, topOffsetPx, onContactClick }) {
    // ✅ Hooks must be called BEFORE any early return.
    // Use safe defaults so hooks can run unconditionally.
    const safeSection = section || {};
    const slider = safeSection.slider || null;
    const sliderItems = Array.isArray(slider?.items) ? slider.items : [];
    const sliderItemsCount = sliderItems.length;

    const sliderSettings = useMemo(() => {
        return {
            dots: true,
            arrows: false,
            infinite: sliderItemsCount > 1,
            speed: 450,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: sliderItemsCount > 1,
            autoplaySpeed: 6500,
            adaptiveHeight: true,
        };
    }, [sliderItemsCount]);

    // now that hooks have run, it’s safe to return early
    if (!safeSection.display) return null;

    const bgStyle = buildBackgroundStyle(safeSection.background);
    const overlayBg = slider?.background || "rgba(0,0,0,.6)";
    const darkText = !!slider?.darkText;

    return (
        <Box
            sx={{
                ...bgStyle,
                display: "flex",
                alignItems: "stretch",
                position: "relative",
                pt: `${topOffsetPx}px`,
            }}
        >
            <Container maxWidth={false} sx={{ flex: 1, py: 4, px: { xs: 2, md: 4 } }}>
                <Box sx={{ display: "flex", justifyContent: "center", height: "100%"}}>
                    {sliderItemsCount ? (
                        <Box
                            sx={{
                                width:
                                    slider?.size === "compact"
                                        ? { xs: "100%", md: "720px" }
                                        : "100%",
                                maxWidth: "1100px",
                                background: overlayBg,
                                borderRadius: 2,
                                p: slider?.padding ?? 2,
                                alignSelf: slider?.bottom ? "flex-end" : "center",
                            }}
                        >
                            <Slider {...sliderSettings}>
                                {sliderItems.map((it, idx) => (
                                    <Box key={idx} sx={{ px: 2, py: 2 }}>
                                        {it?.primaryText ? (
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    color: darkText ? "#111" : "#fff",
                                                    fontWeight: 700,
                                                    mb: 1,
                                                }}
                                            >
                                                {it.primaryText}
                                            </Typography>
                                        ) : null}

                                        {it?.list && Array.isArray(it?.lists) ? (
                                            <Box sx={{ mt: 1 }}>
                                                {it.lists.map((l, li) => (
                                                    <Box key={li} sx={{ mb: 2 }}>
                                                        {l?.title ? (
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: darkText ? "#111" : "#fff",
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {l.title}
                                                            </Typography>
                                                        ) : null}
                                                        {Array.isArray(l?.items) ? (
                                                            <Box
                                                                component="ul"
                                                                sx={{
                                                                    m: 0,
                                                                    pl: 3,
                                                                    color: darkText ? "#111" : "#fff",
                                                                }}
                                                            >
                                                                {l.items.map((x, xi) => (
                                                                    <li key={xi}>
                                                                        <Typography
                                                                            sx={{
                                                                                color: darkText ? "#111" : "#fff",
                                                                            }}
                                                                        >
                                                                            {x}
                                                                        </Typography>
                                                                    </li>
                                                                ))}
                                                            </Box>
                                                        ) : null}
                                                    </Box>
                                                ))}
                                                {it?.bottomText ? (
                                                    <Typography sx={{ color: darkText ? "#111" : "#fff", mt: 1 }}>
                                                        {it.bottomText}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        ) : (
                                            <>
                                                {it?.secondaryText ? (
                                                    <Typography sx={{ color: darkText ? "#111" : "#ddd", mb: 1 }}>
                                                        {it.secondaryText}
                                                    </Typography>
                                                ) : null}
                                                {it?.secondaryText2 ? (
                                                    <Typography sx={{ color: darkText ? "#111" : "#ddd", mb: 1 }}>
                                                        {it.secondaryText2}
                                                    </Typography>
                                                ) : null}
                                                {it?.secondaryText3 ? (
                                                    <Typography sx={{ color: darkText ? "#111" : "#ddd" }}>
                                                        {it.secondaryText3}
                                                    </Typography>
                                                ) : null}
                                            </>
                                        )}

                                        {it?.link?.href ? (
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    component="a"
                                                    href={it.link.href}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Learn more
                                                </Button>
                                            </Box>
                                        ) : null}
                                    </Box>
                                ))}
                            </Slider>

                            {safeSection.contactButton ? (
                                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                                    <Button
                                        variant="contained"
                                        onClick={onContactClick}
                                        sx={{
                                            background:
                                                safeSection.contactButtonBackground ||
                                                site?.buttonColor ||
                                                "#fff",
                                            color:
                                                safeSection.contactButtonColor ||
                                                site?.buttonColorText ||
                                                "#111",
                                        }}
                                    >
                                        {site?.contactButtonLabel || "Contact"}
                                    </Button>
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}
                </Box>
            </Container>
        </Box>
    );
}

function CardsSection({ section, onContactClick }) {
    if (!section?.display) return null;

    const items = Array.isArray(section.items) ? section.items : [];
    const maxRowSize = section.maxRowSize || 4;

    return (
        <Box sx={{ background: section.background || "#f7f7f7", py: 6 }}>
            <Container maxWidth="lg">
                {section.title ? (
                    <Typography variant="h4" sx={{ mb: 1, color: section.titleColor || "#111" }}>
                        {section.title}
                    </Typography>
                ) : null}
                {section.subtitle ? (
                    <Typography sx={{ mb: 3, color: section.subtitleColor || "#333" }}>
                        {section.subtitle}
                    </Typography>
                ) : null}

                <Grid container spacing={3}>
                    {items.map((it, idx) => {
                        const href = normalizeHref(it.href);
                        return (
                            <Grid
                                key={idx}
                                item
                                xs={12}
                                sm={6}
                                md={Math.max(3, Math.floor(12 / maxRowSize))}
                            >
                                <Card elevation={section.shadow ? 8 : 2} sx={{ height: "100%" }}>
                                    <CardActionArea
                                        onClick={() => {
                                            if (href) window.open(href, "_blank", "noreferrer");
                                        }}
                                        sx={{ height: "100%" }}
                                        disabled={!href}
                                    >
                                        {it.image ? (
                                            <CardMedia
                                                component="img"
                                                image={it.image}
                                                alt={it.primary || ""}
                                                height={160}
                                            />
                                        ) : null}
                                        <CardContent>
                                            {it.primary ? (
                                                <Typography variant="h6" sx={{ mb: it.secondary ? 0.5 : 0 }}>
                                                    {it.primary}
                                                </Typography>
                                            ) : null}
                                            {it.secondary ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {it.secondary}
                                                </Typography>
                                            ) : null}
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {section.contactButton ? (
                    <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            onClick={onContactClick}
                            sx={{
                                background: section.contactButtonBackground || "#444",
                                color: section.contactButtonColor || "#fff",
                            }}
                        >
                            Contact
                        </Button>
                    </Box>
                ) : null}
            </Container>
        </Box>
    );
}

function SocialsSection({ section }) {
    if (!section?.display) return null;
    const items = Array.isArray(section.items) ? section.items : [];

    return (
        <Box sx={{ background: section.background || "#050505", py: 5 }}>
            <Container maxWidth="md">
                {section.title ? (
                    <Typography variant="h5" sx={{ mb: 2, color: section.titleColor || "#eee" }}>
                        {section.title}
                    </Typography>
                ) : null}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {items.map((it, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <Typography sx={{ minWidth: 120, color: "#bbb" }}>{it.primary}</Typography>
                            <Link
                                href={it.secondary}
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                                sx={{ color: "#fff", wordBreak: "break-all" }}
                            >
                                {it.secondary}
                            </Link>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
}

function ContactInfoSection({ section }) {
    if (!section?.display) return null;

    const addr = section.address;
    const phone = section.phone;
    const email = section.email;

    return (
        <Box sx={{ background: section.background || "#000", py: 6 }}>
            <Container maxWidth="md">
                {section.title ? (
                    <Typography variant="h4" sx={{ mb: 2, color: section.titleColor || "#ddd" }}>
                        {section.title}
                    </Typography>
                ) : null}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {addr ? (
                        <Typography sx={{ color: "#ddd" }}>
                            {addr.street}
                            <br />
                            {addr.city}, {addr.state} {addr.zip}
                        </Typography>
                    ) : null}

                    {phone ? <Typography sx={{ color: "#ddd" }}>{phone}</Typography> : null}
                    {email ? <Typography sx={{ color: "#ddd" }}>{email}</Typography> : null}

                    {section.officeHours?.items?.length ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ color: "#bbb", mb: 0.5 }}>
                                {section.officeHours.title || "Office Hours"}
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 3, color: "#ddd" }}>
                                {section.officeHours.items.map((x, i) => (
                                    <li key={i}>
                                        <Typography sx={{ color: "#ddd" }}>{x}</Typography>
                                    </li>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            </Container>
        </Box>
    );
}

function FooterSection({ section }) {
    if (!section?.display) return null;

    const partner = section.partner;
    const partnerHref = normalizeHref(partner?.href);

    return (
        <Box sx={{ background: section.background || "#000", py: 4 }}>
            <Container maxWidth="lg">
                <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,.15)" }} />
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {section.logo ? (
                            <img
                                src={section.logo}
                                alt={section.alt || ""}
                                style={{ height: section.logoHeight || "40px" }}
                            />
                        ) : null}
                        {section.title ? (
                            <Typography sx={{ color: section.titleColor || "#ddd" }}>
                                {section.title}
                            </Typography>
                        ) : null}
                    </Box>

                    {partner?.logo ? (
                        partnerHref ? (
                            <a href={partnerHref} target="_blank" rel="noreferrer">
                                <img
                                    src={partner.logo}
                                    alt={partner.alt || "Partner"}
                                    style={{ height: partner.logoHeight || partner.height || "36px" }}
                                />
                            </a>
                        ) : (
                            <img
                                src={partner.logo}
                                alt={partner.alt || "Partner"}
                                style={{ height: partner.logoHeight || partner.height || "36px" }}
                            />
                        )
                    ) : null}
                </Box>
            </Container>
        </Box>
    );
}

export default function Landing({ siteId }) {
    // ✅ hooks first, always
    const site = useMemo(() => getSiteConfig(siteId), [siteId]);
    const [contactOpen, setContactOpen] = useState(false);
    const [contactInfoOpen, setContactInfoOpen] = useState(false);

    const siteFont = site?.fontFamily || "Times New Roman, Times, serif";

    const landingTheme = useMemo(() => {
        return createTheme({
            typography: { fontFamily: siteFont },
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        html: { fontFamily: siteFont },
                        body: { fontFamily: siteFont },
                        "#root": { fontFamily: siteFont },
                        "*, *::before, *::after": { fontFamily: siteFont },
                        "input, textarea, button, select": { fontFamily: siteFont },
                    },
                },
            },
        });
    }, [siteFont]);

    // derived values
    const sections = site?.sections || [];
    const header = sections.find((s) => s.type === "header" && s.display);
    const main = sections.find((s) => s.type === "main" && s.display);
    const footer = sections.find((s) => s.type === "footer" && s.display);
    const headerOffset = header?.fixed ? 64 : 0;
    const contactInfoSection = sections.find((s) => s.type === "contactinfo" && s.display);

    function handleContactClick() {
        setContactOpen(true);
    }

    // ✅ after hooks are declared, safe to early return
    if (!site) {
        return (
            <ThemeProvider theme={landingTheme}>
                <CssBaseline />
                <Box sx={{ p: 4 }}>
                    <Typography variant="h5">Site not found</Typography>
                    <Typography sx={{ mt: 1 }}>Unknown siteId: {String(siteId)}</Typography>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={landingTheme}>
            <CssBaseline />

            <Box sx={{ minHeight: "100vh" }}>
                <Helmet>
                    <title>{site.pageTitle || site.companyName || site.id}</title>
                    {site.meta?.description ? <meta name="description" content={site.meta.description} /> : null}
                </Helmet>

                {header ? (
                    <HeaderSection site={site} section={header} onContactClick={handleContactClick} />
                ) : null}

                {main ? (
                    <MainSection
                        site={site}
                        section={main}
                        topOffsetPx={headerOffset}
                        onContactClick={handleContactClick}
                    />
                ) : null}

                {sections
                    .filter((s) => s.display && !["header", "main", "footer"].includes(s.type))
                    .map((section) => {
                        if (section.type === "cards") {
                            return (
                                <CardsSection
                                    key={section.id}
                                    section={section}
                                    onContactClick={handleContactClick}
                                />
                            );
                        }
                        if (section.type === "socials") {
                            return <SocialsSection key={section.id} section={section} />;
                        }
                        if (section.type === "contactinfo") {
                            return <ContactInfoSection key={section.id} section={section} />;
                        }
                        if (section.type === "partner") {
                            return (
                                <Box key={section.id} sx={{ background: section.background || "#111", py: 6 }}>
                                    <Container maxWidth="md">
                                        {section.title ? (
                                            <Typography variant="h5" sx={{ mb: 2, color: section.titleColor || "#ddd" }}>
                                                {section.title}
                                            </Typography>
                                        ) : null}
                                        {section.partner?.logo ? (
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                {section.partner.href ? (
                                                    <a href={section.partner.href} target="_blank" rel="noreferrer">
                                                        <img
                                                            src={section.partner.logo}
                                                            alt={section.partner.alt || "Partner"}
                                                            style={{ height: section.partner.height || "48px" }}
                                                        />
                                                    </a>
                                                ) : (
                                                    <img
                                                        src={section.partner.logo}
                                                        alt={section.partner.alt || "Partner"}
                                                        style={{ height: section.partner.height || "48px" }}
                                                    />
                                                )}
                                            </Box>
                                        ) : null}
                                    </Container>
                                </Box>
                            );
                        }
                        return null;
                    })}

                {footer ? <FooterSection section={footer} /> : null}

                {site.floatingContactInfoButton && contactInfoSection ? (
                    <Tooltip title="Contact Info">
                        <Fab
                            onClick={() => setContactInfoOpen(true)}
                            sx={{
                                position: "fixed",
                                right: 18,
                                top: 120,
                                background: site.floatingBackground || "rgba(0,0,0,.85)",
                                color: "#fff",
                            }}
                        >
                            i
                        </Fab>
                    </Tooltip>
                ) : null}

                <ContactDialog
                    open={contactOpen}
                    onClose={() => setContactOpen(false)}
                    site={site}
                    siteId={siteId}
                />

                <Dialog open={contactInfoOpen} onClose={() => setContactInfoOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Contact Info</DialogTitle>
                    <DialogContent dividers>
                        {contactInfoSection ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {contactInfoSection.address ? (
                                    <Typography>
                                        {contactInfoSection.address.street}
                                        <br />
                                        {contactInfoSection.address.city}, {contactInfoSection.address.state}{" "}
                                        {contactInfoSection.address.zip}
                                    </Typography>
                                ) : null}
                                {contactInfoSection.phone ? <Typography>{contactInfoSection.phone}</Typography> : null}
                                {contactInfoSection.email ? <Typography>{contactInfoSection.email}</Typography> : null}
                            </Box>
                        ) : (
                            <Typography>No contact info configured.</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setContactInfoOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}